import { useLocation, Link } from "react-router-dom";
import {
  Home,
  Briefcase,
  Calendar,
  CheckSquare,
  MessageSquare,
  Clock,
  FileText,
  HelpCircle,
  BarChart3,
  User,
  Shield,
  UserCheck,
  LogOut,
  GanttChart,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { useTranslation } from "react-i18next";
import logo from "@/assets/Struvent-Logo-2.png";
import { cn } from "@/lib/utils";

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

const mainNav: NavItem[] = [
  { title: "Home", url: "/", icon: Home },
  { title: "Today Board", url: "/today-board", icon: CheckSquare },
  { title: "Jobs", url: "/jobs", icon: Briefcase },
  { title: "Schedule", url: "/schedule", icon: Calendar },
  { title: "Gantt Chart", url: "/gantt", icon: GanttChart },
];

const toolsNav: NavItem[] = [
  { title: "Messages", url: "/messages", icon: MessageSquare },
  { title: "Calendar", url: "/calendar", icon: Calendar },
  { title: "Time Cards", url: "/time-cards", icon: Clock },
  { title: "Crew Availability", url: "/crew-availability", icon: UserCheck },
];

const managementNav: NavItem[] = [
  { title: "RFIs", url: "/rfis", icon: HelpCircle },
  { title: "Estimates", url: "/estimates", icon: FileText },
  { title: "Reports & Analytics", url: "/reports-analytics", icon: BarChart3 },
];

const settingsNav: NavItem[] = [
  { title: "Preferences", url: "/preferences", icon: User },
  { title: "Admin", url: "/admin", icon: Shield },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();

  const isActive = (url: string) =>
    url === "/" ? location.pathname === "/" : location.pathname.startsWith(url);

  const renderItems = (items: NavItem[]) => (
    <SidebarMenu>
      {items.map((item) => (
        <SidebarMenuItem key={item.url}>
          <SidebarMenuButton asChild>
            <Link
              to={item.url}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                isActive(item.url)
                  ? "bg-secondary/20 text-secondary font-semibold"
                  : "text-primary-foreground/90 hover:bg-primary-foreground/10 hover:text-primary-foreground"
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              {!collapsed && <span>{item.title}</span>}
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      ))}
    </SidebarMenu>
  );

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarContent className="bg-primary text-primary-foreground">
        {/* Logo */}
        <div className={cn("flex items-center gap-3 px-4 py-5", collapsed && "justify-center px-2")}>
          <img src={logo} alt="Struvent" className="h-9 w-9 object-contain bg-white rounded-lg p-1.5 shrink-0" />
          {!collapsed && (
            <span className="text-lg font-heading font-bold text-primary-foreground">Struvent</span>
          )}
        </div>

        <SidebarGroup>
          <SidebarGroupLabel className="text-primary-foreground/50 text-xs uppercase tracking-wider">
            {!collapsed && "Main"}
          </SidebarGroupLabel>
          <SidebarGroupContent>{renderItems(mainNav)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-primary-foreground/50 text-xs uppercase tracking-wider">
            {!collapsed && "Tools"}
          </SidebarGroupLabel>
          <SidebarGroupContent>{renderItems(toolsNav)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-primary-foreground/50 text-xs uppercase tracking-wider">
            {!collapsed && "Management"}
          </SidebarGroupLabel>
          <SidebarGroupContent>{renderItems(managementNav)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-primary-foreground/50 text-xs uppercase tracking-wider">
            {!collapsed && "Settings"}
          </SidebarGroupLabel>
          <SidebarGroupContent>{renderItems(settingsNav)}</SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
